import React, { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { PageHeader } from '../components/common/PageHeader';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Slider } from '../components/ui/slider';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { Textarea } from '../components/ui/textarea.jsx';
import CoverEditor from './CoverEditor.jsx';
import html2pdf from 'html2pdf.js';
import { fmtEur } from '../lib/constants';
import { dietIconsHtml } from '../lib/dietTags';
import { QrCode, Printer, Download, Copy, ExternalLink, Check, Eye, Smartphone, ChefHat, BellRing, Table2, FileText, Loader2, Upload, X, Sparkles, RefreshCw, Languages } from 'lucide-react';
import { MenuTranslationsEditor } from '../components/menu/MenuTranslationsEditor.jsx';
import { PaperMenuEditor } from '../components/menu/PaperMenuEditor.jsx';
import { buildMenuBlocks, mergeGroupStyles, loadEditorState, saveEditorState } from '../lib/paperMenuModel';

const DEFAULT_STYLE = { qr_fg: '#1A1A1A', qr_bg: '#FFFFFF', frame_color: '#F2682A', caption: 'Scansiona il QR per il menù', show_logo: true };

const hexToRgba = (hex, a) => {
  const h = String(hex || '').replace('#', '');
  const f = h.length === 3 ? h.split('').map((x) => x + x).join('') : h;
  const n = parseInt(f, 16);
  if (isNaN(n)) return `rgba(0,0,0,${a})`;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

// Temi del menù cartaceo (sfondo pagina + testo). "Colorato" usa fasce colorate per le categorie.
const PAPER_THEMES = {
  light: { pageBg: '#fbf8f2', ink: '#2b2320', muted: '#7a6f66', dots: '#c9bdae', foot: '#e6dccb', bar: false },
  dark: { pageBg: '#17140f', ink: '#f3ece2', muted: '#a99f92', dots: 'rgba(255,255,255,.28)', foot: 'rgba(255,255,255,.16)', bar: false },
  colorful: { pageBg: '#fff5ee', ink: '#2a1c14', muted: '#8a6f5f', dots: null, foot: null, bar: true },
};
// Stili (font + decorazioni intestazioni).
const PAPER_FONTS = {
  simple: { head: "'Montserrat',system-ui,sans-serif", body: "'Montserrat',system-ui,sans-serif", nameWeight: 800, nameSize: '40px', catSize: '19px', ls: '.3px', headBorder: 'single', upperName: false },
  elegant: { head: "'Playfair Display',serif", body: "'EB Garamond',Georgia,'Times New Roman',serif", nameWeight: 800, nameSize: '46px', catSize: '22px', ls: '.5px', headBorder: 'double', upperName: false },
  streetart: { head: "'Anton',system-ui,sans-serif", body: "'Montserrat',system-ui,sans-serif", nameWeight: 400, nameSize: '58px', catSize: '26px', ls: '1.5px', headBorder: 'thick', upperName: true },
};

// Icona (line-art) del tipo di attività, usata come motivo di sfondo.
const bizIconSvg = (key, color) => {
  const S = `stroke='${color}' fill='none' stroke-width='4' stroke-linecap='round' stroke-linejoin='round'`;
  const F = `fill='${color}' stroke='none'`;
  const icons = {
    restaurant: `<g ${S}><path d='M38 24 V76'/><path d='M32 24 V40 Q32 46 38 46 Q44 46 44 40 V24'/><path d='M62 24 Q70 30 68 50 L62 54 V76'/></g>`,
    pizzeria: `<g ${S}><path d='M50 22 L74 74 L26 74 Z'/></g><circle cx='50' cy='46' r='3.5' ${F}/><circle cx='42' cy='62' r='3.5' ${F}/><circle cx='58' cy='62' r='3.5' ${F}/>`,
    cafe: `<g ${S}><path d='M30 44 H66 V62 Q66 72 56 72 H40 Q30 72 30 62 Z'/><path d='M66 48 Q78 48 78 57 Q78 66 66 64'/><path d='M40 30 Q44 36 40 42 M50 30 Q54 36 50 42'/></g>`,
    bar: `<g ${S}><path d='M28 32 H72 L50 56 Z'/><path d='M50 56 V72'/><path d='M38 72 H62'/></g>`,
    pub: `<g ${S}><rect x='32' y='34' width='30' height='42' rx='3'/><path d='M62 42 H74 V60 H62'/><path d='M40 34 V76 M50 34 V76'/></g>`,
    gelateria: `<g ${S}><path d='M36 46 H64 L50 80 Z'/><path d='M36 46 Q36 28 50 28 Q64 28 64 46'/></g>`,
    fish: `<g ${S}><path d='M26 50 Q44 34 64 50 Q44 66 26 50 Z'/><path d='M64 50 L76 40 V60 Z'/></g><circle cx='36' cy='47' r='2.6' ${F}/>`,
    streetfood: `<g ${S}><path d='M28 44 Q50 26 72 44'/><path d='M28 44 H72'/><path d='M30 54 H70'/><path d='M28 60 Q50 74 72 60'/></g>`,
  };
  return icons[key] || icons.restaurant;
};

// Rasterizza un tile SVG in PNG (html2canvas renderizza i PNG in modo affidabile).
const rasterizeSvgTile = (svg, w, h) => new Promise((resolve) => {
  try {
    const url = `data:image/svg+xml,${encodeURIComponent(svg)}`;
    const img = new Image();
    img.onload = () => {
      try {
        const scale = 2;
        const cv = document.createElement('canvas');
        cv.width = w * scale; cv.height = h * scale;
        const ctx = cv.getContext('2d');
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0, w, h);
        resolve(cv.toDataURL('image/png'));
      } catch (e) { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  } catch (e) { resolve(null); }
});

// Campiona la luminosità media di una regione (0..1) di un'immagine data URL,
// per scegliere automaticamente un colore di testo leggibile sopra la grafica AI.
const sampleLuma = (src, rx0, ry0, rx1, ry1) => new Promise((resolve) => {
  try {
    const img = new Image();
    img.onload = () => {
      try {
        const W = 80, H = 120;
        const cv = document.createElement('canvas');
        cv.width = W; cv.height = H;
        const ctx = cv.getContext('2d');
        ctx.drawImage(img, 0, 0, W, H);
        const x0 = Math.floor(rx0 * W), y0 = Math.floor(ry0 * H);
        const w = Math.max(1, Math.ceil(rx1 * W) - x0), h = Math.max(1, Math.ceil(ry1 * H) - y0);
        const d = ctx.getImageData(x0, y0, w, h).data;
        let s = 0, n = 0;
        for (let i = 0; i < d.length; i += 4) { s += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]; n += 1; }
        resolve(n ? (s / n) / 255 : 0.5);
      } catch (e) { resolve(0.5); }
    };
    img.onerror = () => resolve(0.5);
    img.src = src;
  } catch (e) { resolve(0.5); }
});

// ---- Copertina & pagina "Grazie" personalizzabili ----
const COVER_KEYS = ['logo', 'name', 'address', 'phone'];
const THANKS_KEYS = ['logo', 'thankTitle', 'thankMsg', 'name'];
const DEFAULT_POS = {
  logo: { x: 50, y: 20, scale: 1 },
  name: { x: 50, y: 34, scale: 1 },
  address: { x: 50, y: 46, scale: 1 },
  phone: { x: 50, y: 52, scale: 1 },
  thankTitle: { x: 50, y: 40, scale: 1 },
  thankMsg: { x: 50, y: 52, scale: 1 },
};
const DEFAULT_THANKS_OVERRIDE = { logo: { x: 50, y: 22, scale: 1 }, name: { x: 50, y: 62, scale: 1 } };

// Font selezionabili per le scritte (già caricati nel documento).
const FONT_OPTIONS = [
  { id: 'playfair', label: 'Playfair Display', family: "'Playfair Display', serif", weight: 800 },
  { id: 'montserrat', label: 'Montserrat', family: "'Montserrat', sans-serif", weight: 800 },
  { id: 'anton', label: 'Anton', family: "'Anton', sans-serif", weight: 400 },
  { id: 'fredoka', label: 'Fredoka', family: "'Fredoka', sans-serif", weight: 600 },
  { id: 'crimson', label: 'Crimson Text', family: "'Crimson Text', serif", weight: 700 },
  { id: 'garamond', label: 'EB Garamond', family: "'EB Garamond', serif", weight: 700 },
];

const hexLuma = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || '').trim());
  if (!m) return 0.5;
  const r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};
// Ombra/alone per rendere leggibile un testo sopra una grafica (in base al colore del testo).
const haloFor = (hex) => (hexLuma(hex) > 0.6
  ? 'text-shadow:0 2px 14px rgba(0,0,0,.6),0 0 3px rgba(0,0,0,.5);'
  : 'text-shadow:0 1px 10px rgba(255,255,255,.75),0 0 2px rgba(255,255,255,.6);');

// Costruisce le "spec" (stile + html) degli elementi di copertina e ringraziamento
// applicando font e colore scelti. I testi in ctx sono già escapati.
const buildSpecs = (ctx, fontId, color) => {
  const fo = FONT_OPTIONS.find((o) => o.id === fontId) || FONT_OPTIONS[0];
  const headFam = fo.family, headW = fo.weight;
  const tc = (color && color !== 'auto') ? color : (ctx.useAi ? ctx.cInk : ctx.ink);
  const halo = ctx.useAi ? haloFor(tc) : '';
  const upper = ctx.upperName ? 'uppercase' : 'none';
  const titleBase = `font-family:${headFam};font-weight:${headW};letter-spacing:${ctx.ls};color:${tc};text-transform:${upper};line-height:1.06;${halo}`;
  const bodyBase = `font-family:${headFam};font-weight:400;color:${tc};line-height:1.5;${halo}`;
  const logoSpec = ctx.bigLogo ? { wrap: 'line-height:0;', html: `<img src="${ctx.bigLogo}" style="max-height:${ctx.logoMaxH}px;max-width:520px;width:auto;object-fit:contain;display:block;"/>` } : null;
  const coverSpec = {
    logo: logoSpec,
    name: ctx.name ? { wrap: `${titleBase}font-size:calc(${ctx.nameSize} + 6px);`, html: ctx.name } : null,
    address: ctx.addrText ? { wrap: `${bodyBase}font-size:18px;`, html: ctx.addrText } : null,
    phone: ctx.phone ? { wrap: `${bodyBase}font-size:18px;`, html: `☎ ${ctx.phone}` } : null,
  };
  const thanksSpec = {
    logo: logoSpec,
    thankTitle: { wrap: `${titleBase}font-size:${ctx.nameSize};`, html: ctx.thankTitleTxt },
    thankMsg: { wrap: `${bodyBase}font-size:18px;`, html: ctx.thankMsgTxt },
    name: ctx.name ? { wrap: `${titleBase}font-size:22px;`, html: ctx.name } : null,
  };
  return { coverSpec, thanksSpec };
};

// Stato posizioni di un layer (copertina o ringraziamento), unendo salvati + default.
const buildLayerEl = (keys, spec, saved, override) => {
  const el = {};
  keys.forEach((k) => {
    const base = (override && override[k]) || DEFAULT_POS[k] || { x: 50, y: 50, scale: 1 };
    const s = saved && saved[k];
    el[k] = {
      x: (s && typeof s.x === 'number') ? s.x : base.x,
      y: (s && typeof s.y === 'number') ? s.y : base.y,
      scale: (s && typeof s.scale === 'number') ? s.scale : base.scale,
      visible: !!spec[k] && (s ? s.visible !== false : true),
    };
  });
  return el;
};

// Genera l'HTML di un layer (copertina/ringraziamento) posizionando gli elementi in
// modo assoluto. Usato SIA per l'anteprima SIA per il PDF: garantisce il WYSIWYG.
const buildLayerHtml = (keys, layerEl, spec, bgCss, cls) => {
  const els = keys.map((k) => {
    const sp = spec[k]; const cfg = layerEl && layerEl[k];
    if (!sp || !cfg || !cfg.visible) return '';
    return `<div style="position:absolute;left:${cfg.x}%;top:${cfg.y}%;transform:translate(-50%,-50%) scale(${cfg.scale});transform-origin:center;text-align:center;max-width:86%;${sp.wrap}">${sp.html}</div>`;
  }).join('');
  return `<section class="${cls}" style="${bgCss}position:relative;height:297mm;overflow:hidden;box-sizing:border-box;">${els}</section>`;
};



// Font del menù cartaceo, caricati anche nel documento principale (necessari per
// misurare correttamente l'altezza dei blocchi durante l'impaginazione).
const PAPER_FONTS_HREF = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=EB+Garamond:ital,wght@0,400;0,600;1,400&family=Montserrat:wght@400;600;700;800&family=Anton&family=Fredoka:wght@400;600;700&family=Crimson+Text:wght@400;600;700&display=swap';
const ensurePaperFonts = () => {
  if (document.getElementById('vexel-paper-fonts')) return;
  const l = document.createElement('link');
  l.id = 'vexel-paper-fonts'; l.rel = 'stylesheet'; l.href = PAPER_FONTS_HREF;
  document.head.appendChild(l);
};

// Impagina i blocchi del menù in pagine A4 misurando l'altezza reale nel DOM.
// Ritorna un array di pagine, ognuna con gli indici dei blocchi che la compongono.
// Garantisce che il contenuto resti DENTRO i margini (quindi dentro la cornice)
// e che nessun blocco (piatto/intestazione) venga spezzato tra due pagine.
const paginateBlocks = async ({ blocks, fontFamily, ink, firstExtraHtml = '',
  sidePadMm = 30, topPadMm = 30, botPadMm = 30 }) => {
  ensurePaperFonts();
  try {
    if (document.fonts && document.fonts.ready) {
      await Promise.race([document.fonts.ready, new Promise((r) => setTimeout(r, 3500))]);
    }
  } catch (e) { /* noop */ }
  const meas = document.createElement('div');
  meas.style.cssText = `position:fixed;left:-10000px;top:0;width:210mm;box-sizing:border-box;padding:0 ${sidePadMm}mm;font-family:${fontFamily};color:${ink};visibility:hidden;z-index:-1;`;
  document.body.appendChild(meas);
  try {
    const pxPerMm = meas.getBoundingClientRect().width / 210;
    // Margine di sicurezza (4mm) per assorbire piccole differenze di misura e
    // garantire che il contenuto NON tocchi mai la cornice.
    const pageH = (297 - topPadMm - botPadMm - 4) * pxPerMm;
    let firstReserved = 0;
    if (firstExtraHtml) {
      meas.innerHTML = `<div id="_fx">${firstExtraHtml}</div>`;
      firstReserved = meas.querySelector('#_fx').getBoundingClientRect().height;
    }
    // overflow:hidden crea un BFC così i margini dei figli sono inclusi nell'altezza.
    meas.innerHTML = blocks.map((b, i) => `<div data-bi="${i}" style="overflow:hidden;">${b.html}</div>`).join('');
    const nodes = Array.from(meas.querySelectorAll('[data-bi]'));
    const tops = nodes.map((n) => n.offsetTop);
    const hs = nodes.map((n) => n.getBoundingClientRect().height);
    const bottomOf = (i) => tops[i] + hs[i];

    // Raggruppa i blocchi in UNITÀ indivisibili (campo .sg): ogni unità è
    // "intestazione categoria + prodotti diretti" oppure "sottocategoria + prodotti".
    // Un'unità non viene MAI spezzata tra due pagine.
    const units = [];
    let uStart = 0;
    for (let i = 1; i <= blocks.length; i++) {
      if (i === blocks.length || blocks[i].sg !== blocks[uStart].sg) {
        units.push({ start: uStart, end: i - 1, cat: blocks[uStart].g });
        uStart = i;
      }
    }
    // Raggruppa le unità per categoria (per tenere ogni categoria unita quando possibile).
    const catGroups = [];
    let cStart = 0;
    for (let i = 1; i <= units.length; i++) {
      if (i === units.length || units[i].cat !== units[cStart].cat) {
        catGroups.push(units.slice(cStart, i));
        cStart = i;
      }
    }

    const pages = [[]];
    let pi = 0;
    let pageTop = tops[0] || 0;
    const limitFor = (idx) => (idx === 0 ? pageH - firstReserved : pageH);
    const usedRel = () => (pages[pi].length ? bottomOf(pages[pi][pages[pi].length - 1]) - pageTop : 0);
    const remaining = () => limitFor(pi) - usedRel();
    const newPage = (top) => { pages.push([]); pi += 1; pageTop = top; };
    const pushRange = (from, to) => { for (let i = from; i <= to; i++) pages[pi].push(i); };
    const unitH = (u) => bottomOf(u.end) - tops[u.start];

    for (const cat of catGroups) {
      const catStart = cat[0].start;
      const catEnd = cat[cat.length - 1].end;
      const catH = bottomOf(catEnd) - tops[catStart];
      if (catH <= remaining()) {
        // L'intera categoria entra nello spazio rimasto.
        pushRange(catStart, catEnd);
      } else if (catH <= pageH) {
        // Non entra qui ma sta in una pagina: la spostiamo INTERA alla pagina dopo.
        if (pages[pi].length > 0) newPage(tops[catStart]);
        pushRange(catStart, catEnd);
      } else {
        // Categoria più alta di una pagina: colloca UNITÀ per unità, senza mai
        // spezzare una sottocategoria e senza lasciare intestazioni orfane.
        for (const u of cat) {
          const uH = unitH(u);
          if (uH <= pageH) {
            if (uH > remaining() && pages[pi].length > 0) newPage(tops[u.start]);
            pushRange(u.start, u.end);
          } else {
            // Unità più alta di una pagina intera: fallback blocco per blocco.
            for (let i = u.start; i <= u.end; i++) {
              if (bottomOf(i) - pageTop > limitFor(pi) && pages[pi].length > 0) {
                let moved = null;
                const prev = pages[pi];
                if (prev.length && blocks[prev[prev.length - 1]].h) moved = prev.pop();
                newPage(moved != null ? tops[moved] : tops[i]);
                if (moved != null) pages[pi].push(moved);
              }
              pages[pi].push(i);
            }
          }
        }
      }
    }
    // Altezza usata da ogni pagina (per distribuire lo spazio residuo e riempire).
    const usedByPage = pages.map((pg) => (pg.length ? bottomOf(pg[pg.length - 1]) - tops[pg[0]] : 0));
    return { pages, usedByPage, pageH, firstReserved, pxPerMm };
  } finally {
    meas.remove();
  }
};


// Carta cartacea + generatore IA grafica temporaneamente nascosti (da riattivare più avanti).
const PAPER_MENU_FEATURE = false;

export default function CartaDigitalePage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.role === 'admin';
  const [rid, setRid] = useState(null);
  const [style, setStyle] = useState(DEFAULT_STYLE);
  const [stats, setStats] = useState({ published_categories: 0, published_products: 0 });
  const [logo, setLogo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [remote, setRemote] = useState({ enabled: false, mode: 'confirm' });
  const [tables, setTables] = useState([]);
  const [selTable, setSelTable] = useState('');
  // Dimensione stampabile del QR: preset (piccolo/medio/grande) o personalizzato in mm
  const [qrPrintSize, setQrPrintSize] = useState(() => localStorage.getItem('vexel_qr_print_size') || 'md');
  const [qrPrintMm, setQrPrintMm] = useState(() => Number(localStorage.getItem('vexel_qr_print_mm')) || 60);
  const [paperBusy, setPaperBusy] = useState(false);
  const [paperLang, setPaperLang] = useState('es');
  const [paperOpen, setPaperOpen] = useState(false);
  const [paperHtml, setPaperHtml] = useState('');
  const [paperSheetInner, setPaperSheetInner] = useState('');
  const [paperBg, setPaperBg] = useState('#fbf8f2');
  const [paperFileName, setPaperFileName] = useState('menu.pdf');
  // Copertina personalizzabile (editor): pezzi per ricomporre il foglio + stato posizioni.
  const [paperCoverSpec, setPaperCoverSpec] = useState(null);
  const [paperThanksSpec, setPaperThanksSpec] = useState(null);
  const [paperCoverCtx, setPaperCoverCtx] = useState(null);
  const [paperFontId, setPaperFontId] = useState('playfair');
  const [paperColor, setPaperColor] = useState('auto');
  const [paperCoverBg, setPaperCoverBg] = useState('');
  const [paperCoverBgImage, setPaperCoverBgImage] = useState(null);
  const [paperMenuHtml, setPaperMenuHtml] = useState('');
  // ---- Editor WYSIWYG carta cartacea (Fase F) ----
  const [paperBlocks, setPaperBlocks] = useState([]);
  const [paperEditorValue, setPaperEditorValue] = useState({ groupStyles: {}, overrides: {} });
  const [editorMenuHtml, setEditorMenuHtml] = useState('');
  const [paperAiPageBg, setPaperAiPageBg] = useState(null);
  const [paperMenuAccent, setPaperMenuAccent] = useState('#F2682A');
  const [transOpen, setTransOpen] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [paperShell, setPaperShell] = useState(null);
  const [coverEl, setCoverEl] = useState(null);
  const [thanksEl, setThanksEl] = useState(null);
  const previewWrapRef = useRef(null);
  const [previewW, setPreviewW] = useState(760);
  // Impostazioni di default per la tipografia interna (non più selezionabili:
  // la grafica è ora guidata dal brief testuale dell'utente).
  const paperBiz = 'restaurant';
  const paperTheme = 'light';
  const paperStyleVar = 'elegant';
  const [paperDesc, setPaperDesc] = useState(() => localStorage.getItem('vexel_paper_desc') || '');
  const [paperLogo, setPaperLogo] = useState(null);
  const [paperLogoName, setPaperLogoName] = useState('');
  const [paperLogoSize, setPaperLogoSize] = useState(() => Number(localStorage.getItem('vexel_paper_logosize')) || 5);
  const [aiArtOn, setAiArtOn] = useState(() => localStorage.getItem('vexel_ai_art_on') === '1');
  const [aiArt, setAiArt] = useState(null); // { cover, page_bg } (base64 data URL)
  const [aiArtBusy, setAiArtBusy] = useState(false);
  const logoInputRef = useRef(null);
  const digitalLogoInputRef = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const paperFrameRef = useRef(null);
  const canvasWrapRef = useRef(null);
  const tableCanvasRef = useRef(null);
  const allQrRef = useRef(null);
  const firstLoad = useRef(true);

  const publicUrl = rid ? `${window.location.origin}/m/${rid}` : '';
  const tableUrl = (rid && selTable) ? `${window.location.origin}/m/${rid}?t=${selTable}` : '';
  const selTableName = tables.find((t) => t.id === selTable)?.name || '';

  useEffect(() => {
    api.get('/menu-card').then(({ data }) => {
      setRid(data.restaurant_id);
      setStyle({ ...DEFAULT_STYLE, ...(data.style || {}) });
      setStats({ published_categories: data.published_categories, published_products: data.published_products });
      if (data.remote_orders) setRemote(data.remote_orders);
    });
    api.get('/admin/receipt-template').then(({ data }) => setLogo(data?.logo_base64 || null)).catch(() => {});
    api.get('/tables').then(({ data }) => {
      const list = (data || []).filter((t) => !t.merged_hidden);
      setTables(list);
      if (list.length) setSelTable(list[0].id);
    }).catch(() => {});
  }, []);

  const saveRemote = async (patch) => {
    const next = { ...remote, ...patch };
    setRemote(next);
    try { await api.put('/menu-card/remote-orders', patch); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); setRemote(remote); }
  };

  // Rigenera le traduzioni AI: svuota la cache lato server così che vengano
  // ricalcolate da zero alla prossima apertura del menu (digitale e cartaceo).
  const regenerateTranslations = async () => {
    setRegenBusy(true);
    try {
      const { data } = await api.post('/menu-translations/regenerate');
      toast.success(t('cartaDigitale.regenDone', { defaultValue: 'Traduzioni azzerate: verranno rigenerate all\'apertura del menu.' }));
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('cartaDigitale.regenError', { defaultValue: 'Impossibile rigenerare le traduzioni' }));
    } finally {
      setRegenBusy(false);
    }
  };


  // Autosave (debounced) when style changes
  useEffect(() => {
    if (firstLoad.current) { firstLoad.current = false; return; }
    const t = setTimeout(() => { api.put('/menu-card', style).catch(() => {}); }, 700);
    return () => clearTimeout(t);
  }, [style]);

  const set = (patch) => setStyle((s) => ({ ...s, ...patch }));

  const getCanvas = () => canvasWrapRef.current?.querySelector('canvas');

  const downloadPng = () => {
    const canvas = getCanvas();
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = 'qr-carta-digitale.png';
    a.click();
  };

  // Calcola la dimensione stampabile del QR (in mm) e un fattore di scala relativo al medio (60mm).
  const QR_PRESETS = { sm: 40, md: 60, lg: 90 };
  const qrPrintDims = () => {
    const raw = qrPrintSize === 'custom' ? Number(qrPrintMm) : QR_PRESETS[qrPrintSize];
    const qrMm = Math.max(20, Math.min(200, isNaN(raw) ? 60 : raw));
    return { qrMm, k: qrMm / 60 };
  };
  const px = (base, k) => (base * k).toFixed(1);

  const printCard = () => {
    const canvas = getCanvas();
    if (!canvas) return;
    const img = canvas.toDataURL('image/png');
    const { qrMm, k } = qrPrintDims();
    const w = window.open('', '_blank', 'width=480,height=720');
    if (!w) { toast.error(t('cartaDigitale.allowPopups')); return; }
    w.document.write(`<!doctype html><html><head><title>QR Carta Digitale</title>
      <style>
        @page { margin: 16mm; }
        body { font-family: Figtree, system-ui, sans-serif; display:flex; align-items:center; justify-content:center; min-height:100vh; margin:0; }
        .card { text-align:center; border:${px(6, k)}px solid ${style.frame_color}; border-radius:${px(28, k)}px; padding:${px(32, k)}px ${px(28, k)}px; }
        .logo { height:${px(54, k)}px; margin-bottom:${px(8, k)}px; }
        h1 { font-size:${px(26, k)}px; font-weight:900; margin:${px(4, k)}px 0 ${px(14, k)}px; letter-spacing:-0.02em; }
        img.qr { width:${qrMm}mm; height:${qrMm}mm; }
        .cap { margin-top:${px(14, k)}px; font-size:${px(18, k)}px; font-weight:800; color:${style.frame_color}; }
        .url { margin-top:${px(6, k)}px; font-size:${px(12, k)}px; color:#666; word-break:break-all; }
      </style></head><body onload="window.print()">
      <div class="card">
        ${style.show_logo && logo ? `<img class="logo" src="${logo}"/>` : ''}
        <h1>MENÙ</h1>
        <img class="qr" src="${img}"/>
        <div class="cap">${(style.caption || '').replace(/</g, '&lt;')}</div>
        <div class="url">${publicUrl}</div>
      </div></body></html>`);
    w.document.close();
  };

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) {}
  };

  const printQrCard = (img, title, url) => {
    const { qrMm, k } = qrPrintDims();
    const w = window.open('', '_blank', 'width=480,height=720');
    if (!w) { toast.error(t('cartaDigitale.allowPopups')); return; }
    w.document.write(`<!doctype html><html><head><title>QR ${title}</title>
      <style>
        @page { margin: 14mm; }
        body { font-family: Figtree, system-ui, sans-serif; display:flex; align-items:center; justify-content:center; min-height:100vh; margin:0; }
        .card { text-align:center; border:${px(6, k)}px solid ${style.frame_color}; border-radius:${px(28, k)}px; padding:${px(30, k)}px ${px(26, k)}px; }
        .logo { height:${px(48, k)}px; margin-bottom:${px(6, k)}px; }
        h1 { font-size:${px(24, k)}px; font-weight:900; margin:${px(4, k)}px 0 ${px(12, k)}px; letter-spacing:-0.02em; }
        img.qr { width:${qrMm}mm; height:${qrMm}mm; }
        .cap { margin-top:${px(12, k)}px; font-size:${px(17, k)}px; font-weight:800; color:${style.frame_color}; }
        .url { margin-top:${px(6, k)}px; font-size:${px(11, k)}px; color:#666; word-break:break-all; }
      </style></head><body onload="window.print()">
      <div class="card">
        ${style.show_logo && logo ? `<img class="logo" src="${logo}"/>` : ''}
        <h1>${(title || 'MENÙ').replace(/</g, '&lt;')}</h1>
        <img class="qr" src="${img}"/>
        <div class="cap">${(style.caption || '').replace(/</g, '&lt;')}</div>
        <div class="url">${url}</div>
      </div></body></html>`);
    w.document.close();
  };

  const getTableCanvas = () => tableCanvasRef.current?.querySelector('canvas');

  const downloadTablePng = () => {
    const c = getTableCanvas(); if (!c) return;
    const a = document.createElement('a');
    a.href = c.toDataURL('image/png');
    a.download = `qr-${(selTableName || 'tavolo').replace(/\s+/g, '-')}.png`;
    a.click();
  };

  const printTableQr = () => {
    const c = getTableCanvas(); if (!c) return;
    printQrCard(c.toDataURL('image/png'), `Tavolo ${selTableName}`, tableUrl);
  };

  const printAllTables = () => {
    const wrap = allQrRef.current; if (!wrap) return;
    const items = Array.from(wrap.querySelectorAll('[data-table-name]'));
    if (!items.length) { toast.error(t('cartaDigitale.noTable')); return; }
    const { qrMm, k } = qrPrintDims();
    const w = window.open('', '_blank', 'width=560,height=760');
    if (!w) { toast.error(t('cartaDigitale.allowPopups')); return; }
    const cards = items.map((div) => {
      const name = div.getAttribute('data-table-name') || '';
      const c = div.querySelector('canvas');
      if (!c) return '';
      return `<div class="card">
        ${style.show_logo && logo ? `<img class="logo" src="${logo}"/>` : ''}
        <h1>Tavolo ${name.replace(/</g, '&lt;')}</h1>
        <img class="qr" src="${c.toDataURL('image/png')}"/>
        <div class="cap">${(style.caption || '').replace(/</g, '&lt;')}</div>
      </div>`;
    }).join('');
    w.document.write(`<!doctype html><html><head><title>QR Tavoli</title>
      <style>
        @page { margin: 12mm; }
        body { font-family: Figtree, system-ui, sans-serif; margin:0; }
        .card { page-break-after: always; text-align:center; border:${px(6, k)}px solid ${style.frame_color}; border-radius:${px(28, k)}px; padding:${px(28, k)}px; margin:0 auto; }
        .logo { height:${px(46, k)}px; margin-bottom:${px(6, k)}px; }
        h1 { font-size:${px(23, k)}px; font-weight:900; margin:${px(4, k)}px 0 ${px(12, k)}px; }
        img.qr { width:${qrMm}mm; height:${qrMm}mm; }
        .cap { margin-top:${px(12, k)}px; font-size:${px(16, k)}px; font-weight:800; color:${style.frame_color}; }
      </style></head><body onload="window.print()">${cards}</body></html>`);
    w.document.close();
  };

  const imageSettings = (style.show_logo && logo) ? { src: logo, height: 56, width: 56, excavate: true } : undefined;

  const aiAccent = () => style.frame_color || '#C2410C';

  // Carica la grafica AI eventualmente già in cache per il brief corrente (senza generarla).
  useEffect(() => {
    if (!aiArtOn) { setAiArt(null); return; }
    let alive = true;
    api.get('/carta/menu-art', { params: { description: paperDesc, accent_color: aiAccent() } })
      .then(({ data }) => { if (!alive) return; setAiArt(data?.cached ? { cover: data.cover, page_bg: data.page_bg } : null); })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiArtOn]);

  // Genera (force=false: usa cache se presente) o rigenera (force=true) la grafica AI.
  const generateAiArt = async (force = false) => {
    setAiArtBusy(true);
    try {
      const { data } = await api.post('/carta/menu-art', {
        description: paperDesc, accent_color: aiAccent(), restaurant_name: '', force,
      });
      const art = { cover: data.cover, page_bg: data.page_bg };
      setAiArt(art);
      toast.success(data.cached && !force ? t('cartaDigitale.aiArtLoaded') : t('cartaDigitale.aiArtReady'));
      return art;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('cartaDigitale.aiArtError'));
      return null;
    } finally {
      setAiArtBusy(false);
    }
  };

  // Genera una versione cartacea (elegante, stampabile) della carta/menù.
  const generatePaperMenu = async (lang = '') => {
    if (!rid) return;
    setPaperBusy(true);
    try {
      const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
      const { data } = await api.get(`/public/menu/${rid}${qs}`);
      const htmlLang = lang || (data.restaurant?.language || 'it');
      const rest = data.restaurant || {};
      const cats = (data.categories || []).filter(
        (c) => (c.products?.length || (c.subcategories || []).some((s) => s.products?.length)),
      );
      if (!cats.length) { toast.error(t('cartaDigitale.paperEmpty')); setPaperBusy(false); return; }

      const esc = (s) => String(s == null ? '' : s).replace(/[&<>]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch]));
      const money = (v) => fmtEur(v);
      const accent = style.frame_color || '#F2682A';

      // ---- Design in base alle preferenze (tipo attività / tema / stile) ----
      const th = PAPER_THEMES[paperTheme] || PAPER_THEMES.light;
      const f = PAPER_FONTS[paperStyleVar] || PAPER_FONTS.elegant;
      const ink = th.ink, muted = th.muted, pageBg = th.pageBg;
      const dots = th.dots || hexToRgba(accent, 0.4);
      const footC = th.foot || hexToRgba(accent, 0.3);
      const divider = hexToRgba(accent, 0.35);
      const barHeader = th.bar || paperStyleVar === 'streetart';

      // ---- Grafica AI (stile Canva): cover full-bleed + sfondo pagina ----
      let art = aiArt;
      if (aiArtOn && (!art || !art.cover || !art.page_bg)) {
        art = await generateAiArt(false);
      }
      const useAi = !!(aiArtOn && art && art.cover && art.page_bg);

      const catH2 = barHeader
        ? `display:inline-block;background:${accent};color:#fff;font-family:${f.head};font-weight:700;font-size:${f.catSize};margin:0 0 8px;padding:6px 14px;text-transform:uppercase;letter-spacing:${f.ls};border-radius:${paperStyleVar === 'streetart' ? '2px' : '6px'};`
        : `font-family:${f.head};font-weight:700;font-size:${f.catSize};color:${paperStyleVar === 'streetart' ? ink : accent};margin:0 0 6px;padding-bottom:5px;text-transform:uppercase;letter-spacing:${f.ls};border-bottom:1px solid ${divider};`;

      const S = {
        cat: `margin-top:16px;page-break-inside:avoid;break-inside:avoid;`,
        catH2: `${catH2}page-break-after:avoid;break-after:avoid;`,
        subcatH3: `font-family:${f.head};font-size:14.5px;font-weight:600;font-style:${paperStyleVar === 'elegant' ? 'italic' : 'normal'};color:${muted};margin:10px 0 4px;page-break-after:avoid;break-after:avoid;`,
        item: `margin:6px 0;page-break-inside:avoid;break-inside:avoid;`,
        itemHead: `display:flex;align-items:baseline;`,
        itemName: `font-size:14.5px;font-weight:600;color:${ink};`,
        dots: `flex:1 1 auto;border-bottom:1.5px dotted ${dots};margin:0 8px;transform:translateY(-5px);`,
        itemPrice: `font-size:14.5px;font-weight:700;color:${accent};white-space:nowrap;`,
        itemDesc: `font-size:11.5px;color:${muted};margin-top:2px;max-width:92%;line-height:1.35;`,
        itemVar: `font-size:11px;color:${muted};margin-top:2px;font-style:italic;`,
      };
      const renderProduct = (p) => `
        <div class="item" style="${S.item}">
          <div class="item-head" style="${S.itemHead}">
            <span class="item-name" style="${S.itemName}">${esc(p.name)}${p.diet_tags?.length ? '&nbsp;' + dietIconsHtml(p.diet_tags, 13, 3) : ''}</span>
            <span class="dots" style="${S.dots}"></span>
            <span class="item-price" style="${S.itemPrice}">${esc(money(p.price))}</span>
          </div>
          ${p.description ? `<div class="item-desc" style="${S.itemDesc}">${esc(p.description)}</div>` : ''}
          ${p.variants?.length ? `<div class="item-var" style="${S.itemVar}">${p.variants.map((v) => `${esc(v.name)}${Number(v.price_delta) ? ` +${esc(money(v.price_delta))}` : ''}`).join('  ·  ')}</div>` : ''}
        </div>`;
      const renderCat = (cat) => {
        const direct = (cat.products || []).map(renderProduct).join('');
        const subs = (cat.subcategories || []).filter((s) => s.products?.length)
          .map((s) => `<div class="subcat" style="page-break-inside:avoid;break-inside:avoid;"><h3 style="${S.subcatH3}">${esc(s.name)}</h3>${(s.products || []).map(renderProduct).join('')}</div>`).join('');
        return `<section class="cat" style="${S.cat}"><h2 style="${S.catH2}">${esc(cat.name)}</h2>${direct}${subs}</section>`;
      };
      const body = cats.map(renderCat).join('');

      const bigLogo = paperLogo || logo || rest.logo;
      const logoMaxH = Math.round(40 + (Number(paperLogoSize) || 5) * 24);
      const addrText = rest.address ? (esc(rest.address) + (rest.address_line2 ? ', ' + esc(rest.address_line2) : '')) : '';

      const nameCss = `font-family:${f.head};font-weight:${f.nameWeight};font-size:${f.nameSize};line-height:1.04;margin:0;letter-spacing:${f.ls};color:${ink};text-transform:${f.upperName ? 'uppercase' : 'none'};`;
      const headBorderCss = f.headBorder === 'double' ? `border-bottom:3px double ${accent};` : (f.headBorder === 'thick' ? `border-bottom:6px solid ${accent};` : `border-bottom:2px solid ${accent};`);

      // Copertina & "Grazie" personalizzabili: colore adattivo su grafica AI, sfondo, ctx.
      let cInk = ink;
      let coverBgCss = '';
      let coverBgImage = null;
      if (useAi) {
        const coverLuma = await sampleLuma(art.cover, 0.12, 0.05, 0.88, 0.45);
        cInk = coverLuma > 0.55 ? '#1a1a1a' : '#ffffff';
        coverBgCss = `background-image:url('${art.cover}');background-size:cover;background-position:center;`;
        coverBgImage = art.cover;
      }
      // Contesto (testi già escapati) per (ri)costruire le spec al cambio di font/colore.
      const coverCtx = {
        useAi, cInk, ink,
        nameSize: f.nameSize, ls: f.ls, upperName: f.upperName,
        bigLogo, logoMaxH, addrText,
        name: rest.name ? esc(rest.name) : '',
        phone: rest.phone ? esc(rest.phone) : '',
        thankTitleTxt: esc(t('cartaDigitale.thankTitle')),
        thankMsgTxt: esc(t('cartaDigitale.thankMsg')),
      };
      let savedFont = null; let savedColor = null;
      try { savedFont = localStorage.getItem('vexel_paper_font'); } catch (e) { savedFont = null; }
      try { savedColor = localStorage.getItem('vexel_paper_color'); } catch (e) { savedColor = null; }
      const fontId0 = savedFont || (paperStyleVar === 'streetart' ? 'anton' : (paperStyleVar === 'simple' ? 'montserrat' : 'playfair'));
      const color0 = savedColor || 'auto';
      const { coverSpec, thanksSpec } = buildSpecs(coverCtx, fontId0, color0);
      let savedCoverEl = null; let savedThanksEl = null;
      try { savedCoverEl = JSON.parse(localStorage.getItem('vexel_cover_el') || 'null'); } catch (e) { savedCoverEl = null; }
      try { savedThanksEl = JSON.parse(localStorage.getItem('vexel_thanks_el') || 'null'); } catch (e) { savedThanksEl = null; }
      const coverElInit = buildLayerEl(COVER_KEYS, coverSpec, savedCoverEl, null);
      const thanksElInit = buildLayerEl(THANKS_KEYS, thanksSpec, savedThanksEl, DEFAULT_THANKS_OVERRIDE);
      const cover = buildLayerHtml(COVER_KEYS, coverElInit, coverSpec, coverBgCss, 'cover');
      // ---- Sezione menù ----
      const menuTitleHtml = `<div style="text-align:center;margin-bottom:22px;">
          <h2 style="${nameCss}font-size:${f.catSize};letter-spacing:4px;text-transform:uppercase;${headBorderCss}display:inline-block;padding-bottom:8px;color:${ink};">${esc(t('cartaDigitale.menuWord'))}</h2>
        </div>`;
      let menuHtml;
      if (useAi) {
        // Impaginazione a pagine A4: ogni pagina ha la sua cornice e margini fissi,
        // così il testo resta SEMPRE dentro la cornice e i piatti non vengono spezzati.
        const blocks = [];
        cats.forEach((cat, gi) => {
          // Sottogruppo "categoria": intestazione + prodotti diretti (mai spezzato)
          const catSg = `${gi}:cat`;
          blocks.push({ g: gi, sg: catSg, h: true, html: `<h2 style="${S.catH2}margin-top:14px;">${esc(cat.name)}</h2>` });
          (cat.products || []).forEach((p) => blocks.push({ g: gi, sg: catSg, html: renderProduct(p) }));
          // Ogni sottocategoria è un sottogruppo a sé: intestazione + prodotti (mai spezzato)
          (cat.subcategories || []).filter((s) => s.products?.length).forEach((s, si) => {
            const subSg = `${gi}:sub${si}`;
            blocks.push({ g: gi, sg: subSg, h: true, html: `<h3 style="${S.subcatH3}">${esc(s.name)}</h3>` });
            (s.products || []).forEach((p) => blocks.push({ g: gi, sg: subSg, html: renderProduct(p) }));
          });
        });
        const { pages, usedByPage, pageH, firstReserved, pxPerMm } = await paginateBlocks({ blocks, fontFamily: f.body, ink, firstExtraHtml: menuTitleHtml });
        const pageBgCss = `background-image:url('${art.page_bg}');background-size:100% 100%;background-position:center;`;
        menuHtml = pages.map((pg, idx) => {
          // Distribuisce lo spazio residuo tra i blocchi per riempire la pagina
          // (con un limite, così le pagine con pochi piatti non risultano troppo arieggiate).
          const usable = (idx === 0 ? pageH - firstReserved : pageH);
          const childCount = pg.length + (idx === 0 ? 1 : 0);
          const gaps = Math.max(1, childCount - 1);
          const leftoverMm = Math.max(0, (usable - (usedByPage[idx] || 0)) / (pxPerMm || 3.78));
          const gapMm = Math.min(14, leftoverMm / gaps).toFixed(2);
          const inner = (idx === 0 ? menuTitleHtml : '') + pg.map((bi) => blocks[bi].html).join('');
          return `<section class="menu-page" style="${pageBgCss}height:297mm;padding:30mm;box-sizing:border-box;overflow:hidden;display:flex;flex-direction:column;row-gap:${gapMm}mm;">${inner}</section>`;
        }).join('<div class="pb" style="page-break-before:always;height:0;overflow:hidden;"></div>');
      } else {
        menuHtml = `<section class="menu" style="padding:14mm 15mm;box-sizing:border-box;">
        ${menuTitleHtml}
        ${body}
      </section>`;
      }
      // Pagina "Grazie": STESSO sfondo/tema della copertina, elementi personalizzabili.
      const thanks = buildLayerHtml(THANKS_KEYS, thanksElInit, thanksSpec, coverBgCss, 'thanks');

      // Sfondo decorativo interno (motivo icona attività): SOLO quando NON si usa la grafica AI.
      let bgLayerCss = '';
      if (!useAi) {
        const bgConf = ({
          simple: { size: 152, op: 0.05, col: ink, rot: 0 },
          elegant: { size: 132, op: 0.06, col: accent, rot: 0 },
          streetart: { size: 112, op: 0.10, col: accent, rot: -12 },
        })[paperStyleVar] || { size: 140, op: 0.05, col: ink, rot: 0 };
        const tileSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='${bgConf.size}' height='${bgConf.size}' viewBox='0 0 100 100'><g transform='rotate(${bgConf.rot} 50 50)' opacity='${bgConf.op}'>${bizIconSvg(paperBiz, bgConf.col)}</g></svg>`;
        const tilePng = await rasterizeSvgTile(tileSvg, bgConf.size, bgConf.size);
        const bgImgs = [];
        const bgSzs = [];
        if (tilePng) { bgImgs.push(`url("${tilePng}")`); bgSzs.push(`${bgConf.size}px ${bgConf.size}px`); }
        if (paperStyleVar === 'streetart') { bgImgs.push(`repeating-linear-gradient(45deg, ${hexToRgba(accent, 0.06)} 0 12px, rgba(0,0,0,0) 12px 46px)`); bgSzs.push('auto'); }
        bgLayerCss = bgImgs.length ? `background-image:${bgImgs.join(',')};background-repeat:repeat;background-size:${bgSzs.join(',')};` : '';
      }

      const sheetHtml = `<div class="doc" style="width:210mm;box-sizing:border-box;background-color:${pageBg};color:${ink};font-family:${f.body};-webkit-print-color-adjust:exact;print-color-adjust:exact;">
        <div class="content" style="${bgLayerCss}">
          ${cover}
          <div class="pb" style="page-break-before:always;height:0;overflow:hidden;"></div>
          ${menuHtml}
          <div class="pb" style="page-break-before:always;height:0;overflow:hidden;"></div>
          ${thanks}
        </div>
      </div>`;

      const fontLink = '<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=EB+Garamond:ital,wght@0,400;0,600;1,400&family=Montserrat:wght@400;600;700;800&family=Anton&display=swap" rel="stylesheet">';
      const previewHtml = `<!doctype html><html lang="${esc(htmlLang)}"><head><meta charset="utf-8"/>
        <meta name="viewport" content="width=device-width, initial-scale=1"/>
        <title>${esc(t('cartaDigitale.menuWord'))} — ${esc(rest.name || '')}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        ${fontLink}
        <style>
          @page{ size:A4; margin:0; }
          html,body{ margin:0; padding:0; background:${pageBg}; }
          body{ -webkit-print-color-adjust:exact; print-color-adjust:exact; }
          .doc{ margin:0 auto; }
        </style></head>
        <body>${sheetHtml}</body></html>`;

      // Nome file PDF (pulito) in base al ristorante e alla lingua scelta
      const safe = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
      const langTag = htmlLang ? String(htmlLang).toUpperCase() : 'ORIG';
      setPaperFileName(`Menu_${safe(rest.name) || 'ristorante'}_${langTag}.pdf`);
      // Pezzi per ricomporre il foglio (copertina + "Grazie" personalizzabili via editor).
      setPaperCoverCtx(coverCtx);
      setPaperFontId(fontId0);
      setPaperColor(color0);
      setPaperCoverSpec(coverSpec);
      setPaperThanksSpec(thanksSpec);
      setPaperCoverBg(coverBgCss);
      setPaperCoverBgImage(coverBgImage);
      setPaperMenuHtml(menuHtml);
      // ---- Editor WYSIWYG (Fase F): blocchi strutturati + stili salvati ----
      const editorBlocks = buildMenuBlocks(cats, { menuTitle: t('cartaDigitale.menuWord') });
      const savedEd = loadEditorState(rid);
      const gStyles = mergeGroupStyles(savedEd?.groupStyles, accent, ink, muted);
      setPaperBlocks(editorBlocks);
      setPaperEditorValue({ groupStyles: gStyles, overrides: savedEd?.overrides || {} });
      setPaperMenuAccent(accent);
      setPaperAiPageBg(useAi ? art.page_bg : null);
      setEditorMenuHtml('');
      setPaperShell({ pageBg, bgLayerCss, ink, fBody: f.body, htmlLang });
      setCoverEl(coverElInit);
      setThanksEl(thanksElInit);
      setPaperSheetInner(sheetHtml);
      setPaperBg(pageBg);
      setPaperOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('cartaDigitale.paperError'));
    }
    setPaperBusy(false);
  };

  // Salva le modifiche dell'editor WYSIWYG (stili di gruppo + override per blocco).
  const handleEditorChange = (next) => {
    setPaperEditorValue(next);
    saveEditorState(rid, next);
  };

  // Ricompone il foglio completo con copertina e "Grazie" personalizzate (WYSIWYG col PDF).
  const assembleSheet = (cel, tel) => {
    if (!paperShell || !paperCoverSpec) return paperSheetInner;
    const coverHtml = buildLayerHtml(COVER_KEYS, cel || coverEl, paperCoverSpec, paperCoverBg, 'cover');
    const thanksHtml = buildLayerHtml(THANKS_KEYS, tel || thanksEl, paperThanksSpec, paperCoverBg, 'thanks');
    const { pageBg, bgLayerCss, ink, fBody } = paperShell;
    const menuHtml = editorMenuHtml || paperMenuHtml;
    return `<div class="doc" style="width:210mm;box-sizing:border-box;background-color:${pageBg};color:${ink};font-family:${fBody};-webkit-print-color-adjust:exact;print-color-adjust:exact;">
      <div class="content" style="${bgLayerCss}">
        ${coverHtml}
        <div class="pb" style="page-break-before:always;height:0;overflow:hidden;"></div>
        ${menuHtml}
        <div class="pb" style="page-break-before:always;height:0;overflow:hidden;"></div>
        ${thanksHtml}
      </div>
    </div>`;
  };

  // Aggiorna le posizioni della copertina (persistite). null = ripristina i default.
  const handleCoverElChange = (next) => {
    if (next === null) {
      try { localStorage.removeItem('vexel_cover_el'); } catch (e) { /* noop */ }
      setCoverEl(buildLayerEl(COVER_KEYS, paperCoverSpec || {}, null, null));
      return;
    }
    setCoverEl(next);
    try { localStorage.setItem('vexel_cover_el', JSON.stringify(next)); } catch (e) { /* noop */ }
  };

  // Aggiorna le posizioni della pagina "Grazie" (persistite). null = ripristina i default.
  const handleThanksElChange = (next) => {
    if (next === null) {
      try { localStorage.removeItem('vexel_thanks_el'); } catch (e) { /* noop */ }
      setThanksEl(buildLayerEl(THANKS_KEYS, paperThanksSpec || {}, null, DEFAULT_THANKS_OVERRIDE));
      return;
    }
    setThanksEl(next);
    try { localStorage.setItem('vexel_thanks_el', JSON.stringify(next)); } catch (e) { /* noop */ }
  };

  // Ricostruisce le spec (font/colore) di copertina e "Grazie" e le applica live.
  const applyCoverStyle = (fontId, color) => {
    if (!paperCoverCtx) return;
    setPaperFontId(fontId); setPaperColor(color);
    try { localStorage.setItem('vexel_paper_font', fontId); localStorage.setItem('vexel_paper_color', color); } catch (e) { /* noop */ }
    const { coverSpec, thanksSpec } = buildSpecs(paperCoverCtx, fontId, color);
    setPaperCoverSpec(coverSpec);
    setPaperThanksSpec(thanksSpec);
  };

  // Misura la larghezza disponibile per scalare l'anteprima (max = larghezza A4).
  useEffect(() => {
    if (!paperOpen) return undefined;
    const measure = () => {
      const w = previewWrapRef.current ? previewWrapRef.current.clientWidth - 32 : 0;
      if (w > 0) setPreviewW(Math.min(794, w));
    };
    measure();
    const id = setTimeout(measure, 120);
    window.addEventListener('resize', measure);
    return () => { clearTimeout(id); window.removeEventListener('resize', measure); };
  }, [paperOpen]);


  // Attende che l'iframe (anteprima) sia pronto e i font caricati.
  const waitFrameReady = async (frame, timeout = 4000) => {
    const doc = frame?.contentDocument;
    if (!doc) return;
    try {
      if (doc.fonts && doc.fonts.ready) {
        await Promise.race([doc.fonts.ready, new Promise((r) => setTimeout(r, timeout))]);
      } else {
        await new Promise((r) => setTimeout(r, 600));
      }
    } catch (e) { /* noop */ }
  };

  // Stampa: costruisce un iframe temporaneo con la copertina personalizzata, poi stampa.
  const handlePrintPaper = async () => {
    if (!paperShell) return;
    const sheet = assembleSheet(coverEl);
    const html = `<!doctype html><html><head><meta charset="utf-8"/>
      <link href="${PAPER_FONTS_HREF}" rel="stylesheet">
      <style>@page{size:A4;margin:0;} html,body{margin:0;padding:0;background:${paperShell.pageBg};} .doc{margin:0 auto;} body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}</style>
      </head><body>${sheet}</body></html>`;
    const f = document.createElement('iframe');
    f.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(f);
    const d = f.contentWindow.document;
    d.open(); d.write(html); d.close();
    await new Promise((r) => setTimeout(r, 300));
    try {
      if (f.contentWindow.document.fonts && f.contentWindow.document.fonts.ready) {
        await Promise.race([f.contentWindow.document.fonts.ready, new Promise((r) => setTimeout(r, 3000))]);
      }
    } catch (e) { /* noop */ }
    try { f.contentWindow.focus(); f.contentWindow.print(); } catch (e) { toast.error(t('cartaDigitale.paperError')); }
    setTimeout(() => { try { f.remove(); } catch (e) { /* noop */ } }, 1500);
  };

  // Genera un PDF multipagina in formato A4 (senza spezzare i piatti) e lo scarica.
  // Il rendering avviene in un nodo del documento PRINCIPALE (non nell'iframe):
  // così html2canvas applica correttamente stili, colori e font.
  const handleDownloadPdf = async () => {
    if (!paperSheetInner) { toast.error(t('cartaDigitale.pdfError')); return; }
    setPdfBusy(true);
    // Costruisce un contenitore off-screen, con CSS scoping per non toccare l'app.
    const root = document.createElement('div');
    root.id = 'vexel-pdf-root';
    root.style.cssText = `position:fixed;left:-10000px;top:0;z-index:-1;background:${paperBg};`;
    root.innerHTML = assembleSheet(coverEl) || paperSheetInner;
    document.body.appendChild(root);
    try {
      // Attende che i font siano pronti nel documento principale.
      try {
        if (document.fonts && document.fonts.ready) {
          await Promise.race([document.fonts.ready, new Promise((r) => setTimeout(r, 4000))]);
        }
      } catch (e) { /* noop */ }
      await new Promise((r) => setTimeout(r, 300));
      const el = root.querySelector('.doc') || root.firstElementChild;
      // Estende il documento a un numero INTERO di pagine A4: così lo sfondo copre
      // completamente ogni pagina, senza strisce bianche in fondo all'ultima.
      try {
        const pxPerMm = el.getBoundingClientRect().width / 210;
        const pageH = 297 * pxPerMm;
        // Sfondo pagina AI: fissa la dimensione in px così ogni pagina A4 mostra l'immagine intera.
        const menuEl = root.querySelector('.menu');
        if (menuEl && menuEl.style.backgroundImage) {
          menuEl.style.backgroundSize = `${el.getBoundingClientRect().width}px ${pageH}px`;
        }
        const pages = Math.max(1, Math.ceil((el.scrollHeight - 2) / pageH));
        el.style.minHeight = `${pages * pageH}px`;
        await new Promise((r) => setTimeout(r, 50));
      } catch (e) { /* noop */ }
      const opt = {
        margin: 0,
        filename: paperFileName,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: paperBg, windowWidth: el.scrollWidth },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'], avoid: ['.item', '.cat'], before: ['.pb'] },
      };
      await html2pdf().set(opt).from(el).save();
      toast.success(t('cartaDigitale.pdfDownloaded'));
    } catch (e) {
      toast.error(t('cartaDigitale.pdfError'));
    } finally {
      root.remove();
      setPdfBusy(false);
    }
  };

  // Carica un logo (PNG/SVG) per la copertina. Rasterizza a PNG per garantire il rendering nel PDF.
  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    const isPng = /png/i.test(file.type) || /\.png$/i.test(file.name);
    const isSvg = /svg/i.test(file.type) || /\.svg$/i.test(file.name);
    if (!isPng && !isSvg) { toast.error(t('cartaDigitale.logoTypeErr')); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      const img = new Image();
      img.onload = () => {
        try {
          const maxW = 800;
          const w = img.naturalWidth || 512;
          const h = img.naturalHeight || 512;
          const ratio = w ? Math.min(1, maxW / w) : 1;
          const cw = Math.max(1, Math.round(w * ratio));
          const ch = Math.max(1, Math.round(h * ratio));
          const canvas = document.createElement('canvas');
          canvas.width = cw; canvas.height = ch;
          canvas.getContext('2d').drawImage(img, 0, 0, cw, ch);
          setPaperLogo(canvas.toDataURL('image/png'));
        } catch (err) {
          setPaperLogo(src);
        }
        setPaperLogoName(file.name);
      };
      img.onerror = () => { setPaperLogo(src); setPaperLogoName(file.name); };
      img.src = src;
    };
    reader.onerror = () => toast.error(t('cartaDigitale.logoTypeErr'));
    reader.readAsDataURL(file);
  };

  // Logo DEDICATO alla carta digitale (indipendente dal logo del gestore).
  // Accetta PNG/SVG; viene salvato in style.digital_logo e persistito via autosave.
  const onDigitalLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    const isPng = /png/i.test(file.type) || /\.png$/i.test(file.name);
    const isSvg = /svg/i.test(file.type) || /\.svg$/i.test(file.name);
    if (!isPng && !isSvg) { toast.error(t('cartaDigitale.logoTypeErr')); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      if (isSvg) {
        // Gli SVG restano vettoriali: si usa direttamente il data URL.
        set({ digital_logo: src });
        toast.success(t('cartaDigitale.digitalLogoSaved', { defaultValue: 'Logo carta digitale aggiornato' }));
        return;
      }
      const img = new Image();
      img.onload = () => {
        try {
          const maxW = 512;
          const w = img.naturalWidth || 512;
          const h = img.naturalHeight || 512;
          const ratio = w ? Math.min(1, maxW / w) : 1;
          const cw = Math.max(1, Math.round(w * ratio));
          const ch = Math.max(1, Math.round(h * ratio));
          const canvas = document.createElement('canvas');
          canvas.width = cw; canvas.height = ch;
          canvas.getContext('2d').drawImage(img, 0, 0, cw, ch);
          set({ digital_logo: canvas.toDataURL('image/png') });
        } catch (err) {
          set({ digital_logo: src });
        }
        toast.success(t('cartaDigitale.digitalLogoSaved', { defaultValue: 'Logo carta digitale aggiornato' }));
      };
      img.onerror = () => { set({ digital_logo: src }); };
      img.src = src;
    };
    reader.onerror = () => toast.error(t('cartaDigitale.logoTypeErr'));
    reader.readAsDataURL(file);
  };
  const removeDigitalLogo = () => set({ digital_logo: '' });

  return (
    <div className="p-4 md:p-6">
      <PageHeader title={t('cartaDigitale.title')} subtitle={t('cartaDigitale.subtitle')} />

      <div className="space-y-3">
        {/* Personalizzazione QR (pannello fisso, sempre aperto) */}
        <div className="min-w-0 rounded-2xl border bg-card px-5" data-testid="qr-custom-section">
          <div className="flex items-center justify-between gap-3 py-4">
            <span className="font-semibold flex items-center gap-2"><QrCode className="h-4 w-4" /> {t('cartaDigitale.qrCustomization')}</span>
            <a href={publicUrl} target="_blank" rel="noreferrer" className="shrink-0">
              <Button variant="outline" className="rounded-xl" data-testid="open-public-menu"><Eye className="h-4 w-4 mr-2" /> {t('cartaDigitale.previewCard')}</Button>
            </a>
          </div>
          <div className="pb-5">
          <div className="rounded-xl bg-muted/50 p-3 text-sm text-muted-foreground">
            <div className="font-medium text-foreground mb-1">{t('cartaDigitale.whatAppears')}</div>
            {t('cartaDigitale.whatAppearsDesc')}
            <div className="mt-2 flex gap-2">
              <span className="rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-semibold">{t('cartaDigitale.categoriesCount', { count: stats.published_categories })}</span>
              <span className="rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-semibold">{t('cartaDigitale.productsCount', { count: stats.published_products })}</span>
            </div>
          </div>

          {/* Anteprima QR con editor colori a fianco (feedback live) */}
          <div className="mt-5 rounded-2xl border bg-card p-5 flex flex-col lg:flex-row items-center lg:items-stretch gap-8" data-testid="qr-preview-outer">
            <div className="rounded-2xl p-5 flex flex-col items-center shrink-0" style={{ border: `6px solid ${style.frame_color}` }} data-testid="qr-preview-card">
              <div className="font-display font-black text-xl mb-3 tracking-tight">{t('cartaDigitale.menuWord')}</div>
              <div ref={canvasWrapRef} className="rounded-xl overflow-hidden" style={{ background: style.qr_bg, padding: 8 }}>
                <QRCodeCanvas value={publicUrl || 'https://example.com'} size={200} fgColor={style.qr_fg} bgColor={style.qr_bg} level="H" imageSettings={imageSettings} includeMargin={false} />
              </div>
              <div className="mt-3 text-center font-extrabold" style={{ color: style.frame_color }}>{style.caption}</div>
            </div>
            <div className="flex-1 w-full min-w-0 flex flex-col gap-4">
              <div className="font-semibold">{t('cartaDigitale.preview')}</div>
              <div className="grid grid-cols-4 gap-x-3 gap-y-3 flex-1 content-between">
                {/* Riga 1: Color del QR | Fondo del QR | Leyenda (span 2) */}
                <ColorField label={t('cartaDigitale.qrColor')} value={style.qr_fg} onChange={(v) => set({ qr_fg: v })} testId="qr-fg" />
                <ColorField label={t('cartaDigitale.qrBackground')} value={style.qr_bg} onChange={(v) => set({ qr_bg: v })} testId="qr-bg" />
                <div className="col-span-2 min-w-0">
                  <Label className="mb-1.5 block">{t('cartaDigitale.caption')}</Label>
                  <Input value={style.caption || ''} onChange={(e) => set({ caption: e.target.value })} placeholder={t('cartaDigitale.captionPlaceholder')} className="h-[58px]" data-testid="qr-caption" />
                </div>
                {/* Riga 2: Color del marco | Enlace público (span 3 = Fondo + Leyenda) */}
                <ColorField label={t('cartaDigitale.frameColor')} value={style.frame_color} onChange={(v) => set({ frame_color: v })} testId="qr-frame" />
                <div className="col-span-3 min-w-0">
                  <Label className="mb-1.5 block">{t('cartaDigitale.publicLink')}</Label>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={publicUrl} className="font-mono text-xs h-[58px]" data-testid="public-url" />
                    <Button variant="outline" size="icon" className="rounded-lg shrink-0 h-[58px] w-[58px]" onClick={copyUrl} title={t('cartaDigitale.copyLink')}>{copied ? <Check className="h-4 w-4 text-pos-free" /> : <Copy className="h-4 w-4" />}</Button>
                    <a href={publicUrl} target="_blank" rel="noreferrer"><Button variant="outline" size="icon" className="rounded-lg shrink-0 h-[58px] w-[58px]" title={t('cartaDigitale.open')}><ExternalLink className="h-4 w-4" /></Button></a>
                  </div>
                </div>
                {/* Riga 3: Imprimir (span 2) | controlli logo carta digitale (allineati orizzontalmente a Imprimir) */}
                <Button className="rounded-xl w-full col-span-2" onClick={printCard} data-testid="print-qr-button"><Printer className="h-4 w-4 mr-2" /> {t('cartaDigitale.print')}</Button>
                <div className="col-span-2 min-w-0 flex items-center gap-2">
                  <input ref={digitalLogoInputRef} type="file" accept=".png,.svg,image/png,image/svg+xml" className="hidden" onChange={onDigitalLogoFile} data-testid="digital-logo-input" />
                  {style.digital_logo ? (
                    <img src={style.digital_logo} alt="" className="h-12 w-12 shrink-0 rounded-xl border border-border object-contain bg-white p-1" data-testid="digital-logo-preview" />
                  ) : (
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-dashed border-border text-muted-foreground"><QrCode className="h-5 w-5" /></div>
                  )}
                  <Button variant="outline" className="rounded-xl h-12 flex-1 min-w-0" onClick={() => digitalLogoInputRef.current?.click()} data-testid="digital-logo-upload">
                    <Upload className="h-4 w-4 mr-2" /> {style.digital_logo ? t('cartaDigitale.changeLogo', { defaultValue: 'Cambia logo' }) : t('cartaDigitale.uploadLogo')}
                  </Button>
                  {style.digital_logo && (
                    <Button variant="ghost" size="icon" className="rounded-xl shrink-0 h-12 w-12 text-muted-foreground hover:text-destructive" onClick={removeDigitalLogo} data-testid="digital-logo-remove" title={t('cartaDigitale.removeLogo', { defaultValue: 'Rimuovi' })}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {/* Riga 4: PNG (span 2) | Dimensione stampa (span 2, allineata a PNG) */}
                <Button variant="outline" className="rounded-xl w-full col-span-2" onClick={downloadPng} data-testid="download-qr-button"><Download className="h-4 w-4 mr-2" /> PNG</Button>
                <div className="col-span-2 min-w-0 flex flex-col sm:flex-row gap-2 sm:items-center">
                  <Select value={qrPrintSize} onValueChange={(v) => { setQrPrintSize(v); localStorage.setItem('vexel_qr_print_size', v); }}>
                    <SelectTrigger className="rounded-xl w-full flex-1" data-testid="qr-print-size"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sm">{t('cartaDigitale.sizeSmall', { defaultValue: 'Piccolo (40 mm)' })}</SelectItem>
                      <SelectItem value="md">{t('cartaDigitale.sizeMedium', { defaultValue: 'Medio (60 mm)' })}</SelectItem>
                      <SelectItem value="lg">{t('cartaDigitale.sizeLarge', { defaultValue: 'Grande (90 mm)' })}</SelectItem>
                      <SelectItem value="custom">{t('cartaDigitale.sizeCustom', { defaultValue: 'Personalizzato' })}</SelectItem>
                    </SelectContent>
                  </Select>
                  {qrPrintSize === 'custom' && (
                    <div className="flex items-center gap-2">
                      <Input type="number" min={20} max={200} value={qrPrintMm}
                        onChange={(e) => { const v = e.target.value; setQrPrintMm(v); localStorage.setItem('vexel_qr_print_mm', v); }}
                        className="rounded-xl w-28" data-testid="qr-print-mm" />
                      <span className="text-sm text-muted-foreground">mm</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>

      {/* ---- Versione cartacea del menù (nascosta temporaneamente) ---- */}
      {PAPER_MENU_FEATURE && (
      <Accordion type="single" collapsible className="mt-4">
        <AccordionItem value="paper-menu" className="rounded-2xl border bg-card px-5" data-testid="paper-menu-card">
          <AccordionTrigger className="py-4 hover:no-underline"><span className="font-semibold flex items-center gap-2"><FileText className="h-4 w-4" /> {t('cartaDigitale.paperTitle')}</span></AccordionTrigger>
          <AccordionContent className="pb-5">
            <p className="text-sm text-muted-foreground mb-4">{t('cartaDigitale.paperDesc')}</p>

            {/* Barra azioni: lingua + generazione + traduzioni (griglia allineata) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Select value={paperLang} onValueChange={setPaperLang}>
                <SelectTrigger className="rounded-xl h-11 w-full" data-testid="paper-menu-lang"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="es" data-testid="paper-lang-es">{t('cartaDigitale.langSpanish')}</SelectItem>
                  <SelectItem value="en" data-testid="paper-lang-en">{t('cartaDigitale.langEnglish')}</SelectItem>
                </SelectContent>
              </Select>
              <Button className="rounded-xl h-11 w-full" onClick={() => generatePaperMenu(paperLang)} disabled={paperBusy} data-testid="generate-paper-menu">
                <FileText className="h-4 w-4 mr-2" /> {paperBusy ? t('cartaDigitale.generating') : t('cartaDigitale.generatePaper')}
              </Button>
              <Button variant="outline" className="rounded-xl h-11 w-full" onClick={regenerateTranslations} disabled={regenBusy} data-testid="regenerate-translations">
                <RefreshCw className={`h-4 w-4 mr-2 ${regenBusy ? 'animate-spin' : ''}`} /> {t('cartaDigitale.regenTranslations', { defaultValue: 'Rigenera traduzioni AI' })}
              </Button>
              <Button variant="outline" className="rounded-xl h-11 w-full" onClick={() => setTransOpen(true)} data-testid="open-translations-editor">
                <Languages className="h-4 w-4 mr-2" /> {t('cartaDigitale.editTranslations', { defaultValue: 'Correggi traduzioni' })}
              </Button>
            </div>

            {/* Descrizione per la grafica AI */}
            <div className="mt-4">
              <Label className="text-xs text-muted-foreground mb-1.5 block" htmlFor="paper-desc">{t('cartaDigitale.aiArtDescLabel')}</Label>
              <Textarea
                id="paper-desc"
                value={paperDesc}
                onChange={(e) => { setPaperDesc(e.target.value); localStorage.setItem('vexel_paper_desc', e.target.value); }}
                placeholder={t('cartaDigitale.aiArtDescPlaceholder')}
                className="rounded-xl min-h-[90px] resize-y"
                data-testid="paper-desc-input"
              />
            </div>

            {/* Logo + dimensione (griglia allineata) */}
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">{t('cartaDigitale.uploadLogo')}</Label>
                <input ref={logoInputRef} type="file" accept=".png,.svg,image/png,image/svg+xml" className="hidden" onChange={onLogoFile} data-testid="paper-logo-input" />
                <div className="flex items-center gap-2">
                  <Button variant="outline" className="rounded-xl h-11 flex-1 min-w-0" onClick={() => logoInputRef.current?.click()} data-testid="paper-logo-upload">
                    <Upload className="h-4 w-4 mr-2" /> <span className="truncate">{paperLogo ? (paperLogoName || t('cartaDigitale.changeLogo', { defaultValue: 'Cambia logo' })) : t('cartaDigitale.uploadLogo')}</span>
                  </Button>
                  {paperLogo && (
                    <>
                      <img src={paperLogo} alt="" className="h-11 w-11 shrink-0 object-contain rounded-xl border bg-white p-1" data-testid="paper-logo-info" />
                      <Button variant="ghost" size="icon" className="h-11 w-11 shrink-0 rounded-xl text-muted-foreground hover:text-destructive" onClick={() => { setPaperLogo(null); setPaperLogoName(''); }} data-testid="paper-logo-remove"><X className="h-4 w-4" /></Button>
                    </>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1.5 block">{t('cartaDigitale.logoSize')}</Label>
                <div className="flex items-center gap-3 h-11 rounded-xl border px-3">
                  <Slider value={[paperLogoSize]} onValueChange={(v) => { setPaperLogoSize(v[0]); localStorage.setItem('vexel_paper_logosize', String(v[0])); }} min={1} max={10} step={1} className="flex-1" data-testid="paper-logo-size" />
                  <span className="text-sm font-semibold w-4 text-center">{paperLogoSize}</span>
                </div>
              </div>
            </div>

            {/* Blocco grafica AI: header + anteprima grande a sinistra, controlli a destra */}
            <div className="mt-4 rounded-xl border p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> {t('cartaDigitale.aiArtTitle')}</div>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('cartaDigitale.aiArtDesc')}</p>
                </div>
                <Switch checked={aiArtOn} onCheckedChange={(v) => { setAiArtOn(v); localStorage.setItem('vexel_ai_art_on', v ? '1' : '0'); }} data-testid="ai-art-toggle" />
              </div>
              {aiArtOn && (
                <div className="mt-4 flex flex-col lg:flex-row gap-6 lg:items-center">
                  {/* Anteprima grande (riempie lo spazio a sinistra, centrata) */}
                  <div className="flex-1 min-w-0 flex gap-3 justify-center" data-testid="ai-art-preview">
                    {aiArt ? (
                      <>
                        <img src={aiArt.cover} alt="cover" className="h-72 w-auto rounded-lg border bg-white object-cover shadow-sm" style={{ aspectRatio: '210 / 297' }} />
                        <img src={aiArt.page_bg} alt="page background" className="h-72 w-auto rounded-lg border bg-white object-cover shadow-sm" style={{ aspectRatio: '210 / 297' }} />
                      </>
                    ) : (
                      <div className="h-72 w-[200px] rounded-lg border border-dashed flex items-center justify-center text-center text-xs text-muted-foreground px-3">{t('cartaDigitale.aiArtEmpty')}</div>
                    )}
                  </div>
                  {/* Controlli AI (centrati verticalmente accanto all'anteprima) */}
                  <div className="w-full lg:w-72 shrink-0 flex flex-col gap-3">
                    <Button variant="outline" className="rounded-xl w-full" onClick={() => generateAiArt(false)} disabled={aiArtBusy} data-testid="ai-art-generate">
                      {aiArtBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                      {aiArtBusy ? t('cartaDigitale.aiArtGenerating') : t('cartaDigitale.aiArtGenerate')}
                    </Button>
                    {aiArt && (
                      <Button variant="ghost" className="rounded-xl w-full" onClick={() => generateAiArt(true)} disabled={aiArtBusy} data-testid="ai-art-regenerate">
                        <RefreshCw className="h-4 w-4 mr-2" /> {t('cartaDigitale.aiArtRegenerate')}
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground">{aiArt ? t('cartaDigitale.aiArtPreviewHint') : t('cartaDigitale.aiArtDesc')}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Nota informativa */}
            <div className="mt-4 rounded-xl bg-muted/50 p-3 text-xs text-muted-foreground">
              {t('cartaDigitale.paperHint')}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      )}

      {/* ---- Anteprima menù cartaceo: stampa (senza popup) + download PDF A4 ---- */}
      <Dialog open={paperOpen} onOpenChange={setPaperOpen}>
        <DialogContent className="max-w-4xl w-[95vw] p-0 gap-0 overflow-hidden" data-testid="paper-preview-dialog">
          <DialogHeader className="px-5 py-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" /> {t('cartaDigitale.paperPreviewTitle')}
            </DialogTitle>
          </DialogHeader>
          {/* Barra stile: font e colore delle scritte (copertina + Grazie) */}
          {paperCoverSpec && (
            <div className="px-5 py-3 border-b flex flex-wrap items-center gap-x-4 gap-y-2 bg-background">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">{t('cartaDigitale.coverFont')}</Label>
                <Select value={paperFontId} onValueChange={(v) => applyCoverStyle(v, paperColor)}>
                  <SelectTrigger className="h-8 rounded-lg w-[168px]" data-testid="cover-font-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FONT_OPTIONS.map((o) => (
                      <SelectItem key={o.id} value={o.id}><span style={{ fontFamily: o.family }}>{o.label}</span></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">{t('cartaDigitale.coverColor')}</Label>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => applyCoverStyle(paperFontId, 'auto')} className={`h-7 rounded-md border px-2 text-xs ${paperColor === 'auto' ? 'ring-2 ring-primary' : ''}`} data-testid="cover-color-auto">{t('cartaDigitale.colorAuto')}</button>
                  {['#ffffff', '#1a1a1a', style.frame_color].map((sw, i) => (
                    <button key={i} type="button" onClick={() => applyCoverStyle(paperFontId, sw)} title={sw} className={`h-7 w-7 rounded-md border ${paperColor === sw ? 'ring-2 ring-primary' : ''}`} style={{ background: sw }} data-testid={`cover-color-${i}`} />
                  ))}
                  <input type="color" value={paperColor !== 'auto' && /^#/.test(paperColor) ? paperColor : '#000000'} onChange={(e) => applyCoverStyle(paperFontId, e.target.value)} className="h-7 w-9 rounded-md border cursor-pointer p-0.5" title={t('cartaDigitale.coverColor')} data-testid="cover-color-custom" />
                </div>
              </div>
            </div>
          )}
          <div ref={previewWrapRef} className="bg-muted/40 overflow-auto p-4 space-y-3" style={{ height: '62vh' }}>
            {paperCoverSpec && coverEl && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground">{t('cartaDigitale.pageCover')}</div>
                <CoverEditor
                  spec={paperCoverSpec}
                  value={coverEl}
                  onChange={handleCoverElChange}
                  keys={COVER_KEYS}
                  bg={{ image: paperCoverBgImage, color: (paperShell && paperShell.pageBg) || paperBg }}
                  width={previewW}
                  labels={{
                    hint: t('cartaDigitale.coverHint'), reset: t('cartaDigitale.coverReset'),
                    remove: t('cartaDigitale.coverRemove'), resize: t('cartaDigitale.coverResize'),
                    names: { logo: t('cartaDigitale.coverLogo'), name: t('cartaDigitale.coverName'), address: t('cartaDigitale.coverAddress'), phone: t('cartaDigitale.coverPhone') },
                  }}
                />
              </div>
            )}
            {paperBlocks.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground">{t('cartaDigitale.editor.menuPages', { defaultValue: 'Pagine menu (modificabili)' })}</div>
                <PaperMenuEditor
                  blocks={paperBlocks}
                  value={paperEditorValue}
                  onChange={handleEditorChange}
                  onHtmlChange={setEditorMenuHtml}
                  accent={paperMenuAccent}
                  money={fmtEur}
                  aiPageBg={paperAiPageBg}
                  width={previewW}
                  t={t}
                />
              </div>
            )}
            {paperThanksSpec && thanksEl && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground">{t('cartaDigitale.pageThanks')}</div>
                <CoverEditor
                  spec={paperThanksSpec}
                  value={thanksEl}
                  onChange={handleThanksElChange}
                  keys={THANKS_KEYS}
                  bg={{ image: paperCoverBgImage, color: (paperShell && paperShell.pageBg) || paperBg }}
                  width={previewW}
                  labels={{
                    hint: t('cartaDigitale.coverHint'), reset: t('cartaDigitale.coverReset'),
                    remove: t('cartaDigitale.coverRemove'), resize: t('cartaDigitale.coverResize'),
                    names: { logo: t('cartaDigitale.coverLogo'), thankTitle: t('cartaDigitale.coverThankTitle'), thankMsg: t('cartaDigitale.coverThankMsg'), name: t('cartaDigitale.coverName') },
                  }}
                />
              </div>
            )}
          </div>
          <DialogFooter className="px-5 py-4 border-t flex-row justify-end gap-2 sm:gap-2">
            <Button variant="outline" className="rounded-xl" onClick={handlePrintPaper} data-testid="paper-print-button">
              <Printer className="h-4 w-4 mr-2" /> {t('cartaDigitale.printMenu')}
            </Button>
            <Button className="rounded-xl" onClick={handleDownloadPdf} disabled={pdfBusy} data-testid="paper-download-pdf">
              {pdfBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              {pdfBusy ? t('cartaDigitale.generatingPdf') : t('cartaDigitale.downloadPdf')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Remote ordering (admin only) ---- */}
      {isAdmin && (
        <Accordion type="single" collapsible className="mt-4">
          <AccordionItem value="remote-orders" className="rounded-2xl border bg-card px-5" data-testid="remote-orders-card">
            <AccordionTrigger className="py-4 hover:no-underline"><span className="font-semibold flex items-center gap-2"><Smartphone className="h-4 w-4" /> {t('cartaDigitale.remoteTitle')}</span></AccordionTrigger>
            <AccordionContent className="pb-5">
              <p className="text-sm text-muted-foreground mb-4">{t('cartaDigitale.remoteDesc')}</p>

          <div className="flex items-center justify-between rounded-xl border border-border p-3.5">
            <div className="min-w-0 pr-3">
              <div className="font-medium">{t('cartaDigitale.remoteEnable')}</div>
              <div className="text-xs text-muted-foreground">{t('cartaDigitale.remoteEnableDesc')}</div>
            </div>
            <Switch checked={!!remote.enabled} onCheckedChange={(v) => saveRemote({ enabled: v })} data-testid="remote-enabled-switch" />
          </div>

          {remote.enabled && (
            <div className="mt-3">
              <Label className="mb-1.5 block">{t('cartaDigitale.remoteHow')}</Label>
              <div className="grid sm:grid-cols-2 gap-2">
                <button
                  onClick={() => saveRemote({ mode: 'confirm' })}
                  data-testid="remote-mode-confirm"
                  className={`text-left rounded-xl border p-3 transition-colors ${remote.mode === 'confirm' ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'}`}
                >
                  <div className="flex items-center gap-2 font-semibold"><BellRing className="h-4 w-4 text-primary" /> {t('cartaDigitale.remoteConfirm')}</div>
                  <div className="text-xs text-muted-foreground mt-1">{t('cartaDigitale.remoteConfirmDesc')}</div>
                </button>
                <button
                  onClick={() => saveRemote({ mode: 'direct' })}
                  data-testid="remote-mode-direct"
                  className={`text-left rounded-xl border p-3 transition-colors ${remote.mode === 'direct' ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'}`}
                >
                  <div className="flex items-center gap-2 font-semibold"><ChefHat className="h-4 w-4 text-primary" /> {t('cartaDigitale.remoteDirect')}</div>
                  <div className="text-xs text-muted-foreground mt-1">{t('cartaDigitale.remoteDirectDesc')}</div>
                </button>
              </div>
            </div>
          )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      {/* ---- Per-table QR codes ---- */}
      {remote.enabled && tables.length > 0 && (
        <Accordion type="single" collapsible className="mt-4">
          <AccordionItem value="table-qr" className="rounded-2xl border bg-card px-5" data-testid="table-qr-card">
            <AccordionTrigger className="py-4 hover:no-underline"><span className="font-semibold flex items-center gap-2"><Table2 className="h-4 w-4" /> {t('cartaDigitale.tableQrTitle')}</span></AccordionTrigger>
            <AccordionContent className="pb-5">
              <p className="text-sm text-muted-foreground mb-4">{t('cartaDigitale.tableQrDesc')}</p>

          <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
            <div>
              <Label className="mb-1.5 block">{t('cartaDigitale.table')}</Label>
              <Select value={selTable} onValueChange={setSelTable}>
                <SelectTrigger data-testid="table-qr-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tables.map((tbl) => <SelectItem key={tbl.id} value={tbl.id}>{tbl.name}</SelectItem>)}
                </SelectContent>
              </Select>

              <div className="mt-3">
                <Label className="mb-1.5 block">{t('cartaDigitale.tableLink')}</Label>
                <Input readOnly value={tableUrl} className="font-mono text-xs" data-testid="table-qr-url" />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button className="rounded-xl" onClick={printTableQr} data-testid="print-table-qr"><Printer className="h-4 w-4 mr-2" /> {t('cartaDigitale.printTable')}</Button>
                <Button variant="outline" className="rounded-xl" onClick={downloadTablePng} data-testid="download-table-qr"><Download className="h-4 w-4 mr-2" /> PNG</Button>
                <Button variant="outline" className="rounded-xl" onClick={printAllTables} data-testid="print-all-tables"><Printer className="h-4 w-4 mr-2" /> {t('cartaDigitale.printAllTables')}</Button>
              </div>
            </div>

            <div className="flex flex-col items-center">
              <div className="rounded-2xl p-4 flex flex-col items-center w-full" style={{ border: `6px solid ${style.frame_color}` }}>
                <div className="font-display font-black mb-2 tracking-tight">{t('cartaDigitale.table')} {selTableName}</div>
                <div ref={tableCanvasRef} className="rounded-xl overflow-hidden" style={{ background: style.qr_bg, padding: 8 }}>
                  {tableUrl && <QRCodeCanvas value={tableUrl} size={180} fgColor={style.qr_fg} bgColor={style.qr_bg} level="H" imageSettings={imageSettings} includeMargin={false} />}
                </div>
              </div>
            </div>
          </div>

          {/* Hidden canvases for "print all" */}
          <div ref={allQrRef} className="hidden" aria-hidden>
            {tables.map((tbl) => (
              <div key={tbl.id} data-table-name={tbl.name}>
                <QRCodeCanvas
                  value={`${window.location.origin}/m/${rid}?t=${tbl.id}`}
                  size={280}
                  fgColor={style.qr_fg}
                  bgColor={style.qr_bg}
                  level="H"
                  imageSettings={imageSettings}
                  includeMargin={false}
                />
              </div>
            ))}
          </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
      </div>

      <MenuTranslationsEditor open={transOpen} onOpenChange={setTransOpen} />
    </div>
  );
}

const ColorField = ({ label, value, onChange, testId }) => (
  <div>
    <Label className="mb-1.5 block">{label}</Label>
    <div className="flex items-center gap-2 rounded-xl border border-border p-1.5">
      <label className="relative h-8 w-10 rounded-lg cursor-pointer border border-border overflow-hidden shrink-0" style={{ background: value }}>
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" data-testid={testId} />
      </label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="border-0 font-mono text-xs h-8 px-1 focus-visible:ring-0" />
    </div>
  </div>
);
